import { Fragment, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useApp } from './store';
import { accounts } from './backend';
import { combo } from './platform';
import { isProjectMode } from './lifecycle';
import Expander from './components/Expander';
import Board from './components/Board';
import PlanView from './components/PlanView';
import DecisionsView from './components/DecisionsView';
import RiskRegisterView from './components/RiskRegister';
import LessonsView from './components/LessonsView';
import LessonsLibrary from './components/LessonsLibrary';
import BudgetPay from './components/BudgetPay';
import Backlog from './components/Backlog';
import Sprints from './components/Sprints';
import Releases from './components/Releases';
import Documents from './components/Documents';
import Activity, { buildFeed } from './components/Activity';
import Dashboard from './components/Dashboard';
import MyWork from './components/MyWork';
import Portfolio from './components/Portfolio';
import Payments from './components/Payments';
import PaymentApproval from './components/PaymentApproval';
import Settings from './components/Settings';
import HelpView from './components/HelpView';
import Admin from './components/Admin';
import MemberPeople from './components/MemberPeople';
import WorkspaceHub from './components/WorkspaceHub';
import Approvals from './components/Approvals';
import Account from './components/Account';
import IssueModal from './components/IssueModal';
import ConfirmHost from './components/ConfirmHost';
import NewBoardFlow from './components/NewBoardFlow';
import SetupWizard from './components/SetupWizard';
import Palette from './components/Palette';
import CoachMark from './components/CoachMark';
import Login from './components/Login';
import VerifyEmail from './components/VerifyEmail';
import Icon from './components/Icon';
import NavIcon from './components/NavIcon';
import { WayBack } from './components/InsightPanels';
import BrandHome from './components/BrandHome';
import VersionChip from './components/VersionChip';
import AccountPanel from './components/AccountPanel';
import EnvBanner from './components/EnvBanner';
import UpdateBanner from './components/UpdateBanner';
import Eula from './components/Eula';
import ChoosePassword from './components/ChoosePassword';
import { shouldShowEula } from './eula';
import { amStaff } from './workspace';
import Switcher from './components/Switcher';
import SampleStrip, { useSampleBoard } from './components/SampleStrip';
import CompanyNotYours from './components/CompanyNotYours';
import WhichCompany from './components/WhichCompany';
import { companyInAddress, addressFor, whichCompany } from './company';
import StartWorkspace from './components/StartWorkspace';
import Feedback from './components/Feedback';
import WhyClosed from './components/WhyClosed';
import Welcome from './components/Welcome';
import NotificationBell from './components/NotificationBell';
import AccountMenu from './components/AccountMenu';
import type { View } from './store';
import { VIEW_TITLES } from './view-titles';

/*
 * SCREENS THAT CARRY THEIR OWN HEADER. Budget & payments renders a rich `fd-head`
 * of its own — the screen title, plus the on-budget pill and an Edit button — in
 * every state (BudgetPay only early-returns when there is no project), so the
 * topbar's generic 19px <h1> was a SECOND title on the same screen; Vinnie caught
 * that duplicate (9 Oct 2026). For this view the screen's own header is the single
 * title and the topbar <h1> is suppressed; the topbar keeps its other controls.
 *
 * Authorised to pay (Payments) and Payment approval do the same: each renders a
 * rich card/fd-head header naming the screen (Payments adds the live count + total,
 * Payment approval adds the chain-status pill) in EVERY state, so the topbar <h1>
 * was a second copy of the same words. Both carry their own header unconditionally,
 * so the topbar title is suppressed for them too. (payapproval added 12 Oct 2026
 * with the chain builder; payments' long-standing duplicate caught on the finance
 * walk the same day.)
 *
 * Documents and Releases are NOT here on purpose. Documents has no title of its
 * own by design (Documents.tsx: "the topbar already names this screen"), and
 * Releases titles itself only once it has builds — its empty state does not — so
 * suppressing the topbar <h1> for either would leave it titleless. They rely on
 * the topbar title in every state, exactly like Board and Backlog; Releases drops
 * its own duplicate <h2> so the topbar title is the only one. (9 Oct 2026.)
 *
 * The Learning & Governance screens — Decisions, Lessons and the Lessons Library —
 * each render their own `fd-title` header (screen title + InfoTip + subtitle, plus
 * "Log a decision" / "Add a lesson") in EVERY state: the list, AND the compose/edit
 * form (DecisionForm and LessonForm both title themselves), so suppressing the
 * topbar <h1> never leaves them titleless. The topbar <h1> was a second copy of
 * "Decisions" / "Lessons" / "Lessons Library" — the same duplicate as Budget, just
 * never fixed. Added here so the screen's own header is the only title. (Vinnie
 * caught it on the governance walk, 25 Oct 2026.)
 */
const OWNS_HEADER = new Set([
  'budgetpay',
  'payments',
  'payapproval',
  'help',
  'decisions',
  'lessons',
  'lessonslibrary',
]);
/*
 * THE CROSS-BOARD AND WORKSPACE VIEWS — the screens that are NOT "this board".
 *
 * A sample board is read-only, and its treatment (the strip across the top, the
 * inert New-issue button) belongs on the sample's OWN screens. These views span
 * every board or the whole workspace — My work, the Portfolio, the lessons
 * library, the finance queues, Workspace and Settings — so even while a sample
 * board is the one selected, they are not showing the sample and must not wear
 * its read-only skin. The person's real work on their real boards shows through
 * here unchanged. (The write guard in the store is keyed per target board, so
 * it already leaves these alone — this only governs the on-screen treatment.)
 */
const CROSS_BOARD = new Set<View>(['mywork', 'portfolio', 'lessonslibrary', 'payments', 'payapproval', 'admin', 'requests', 'settings']);
import { can, type Capability } from './permissions';
import { outstandingSignoffCount, readPolicy, usesSlots } from './signoff-policy';
import { preparedSprintSignoffs } from './documents';
import { canCreateIssues, DEFAULT_SOURCE_NAME, type IssueSource } from './ownership';
import { offersFeedback } from './feedback';
import { previewBanner } from './viewas';
import { displayName, mustChoosePassword, amSuspended } from './people';
import { inboxActionCount } from './notifications';
import { forced } from './env';
import { readCollapsedGroups, writeCollapsedGroups } from './nav-groups';

/*
 * A LINK STRAIGHT TO ONE CARD — #413.
 *
 * The call-out digest lists the cards you were named on, and each reference in
 * it (DOS-42) is a link to exactly that card, not just to the app. The link is
 *   /?card=<issue-id>&board=<board-id>
 * and it is read ONCE, here at module load, before anything can strip it — the
 * same reason the signing page is decided in main.tsx. Captured now, it
 * survives a sign-in on the way in (the app never navigates, so the query stays
 * put) and is acted on once the session and the board list have arrived.
 *
 * It carries the two ids the app already routes by — the board to switch to and
 * the card to open — so nothing has to be looked up in a feed that may not be
 * loaded yet. A person who is not a member of that board is simply landed in the
 * app; the card is never forced open on a board whose issues will never arrive.
 */
const cardLink = (() => {
  try {
    const p = new URLSearchParams(window.location.search);
    const card = p.get('card');
    if (!card) return null;
    return { card, board: p.get('board') || '' };
  } catch {
    return null;
  }
})();
let cardLinkFired = false;

/* The sidebar is grouped so the flat rows read as four short lists: the work
 * you do, the money, what you deliver, and the team around it — Settings sits on
 * its own at the foot. A group header is drawn only when the group actually has
 * a visible row, so a filtered-out Sprints (flow board) never leaves an empty
 * "Work" heading. Group order follows the order each group first appears in NAV
 * below (Work · Finances · Deliver · Team · Settings) — R20. */
type NavGroup = 'MyWork' | 'Work' | 'Finances' | 'Deliver' | 'Team' | 'Settings';

const NAV: {
  view: View;
  label: string;
  group: NavGroup;
  needs?: Capability;
  /**
   * SHOWN ONLY ON A BOARD THAT WORKS IN TIMEBOXES.
   *
   * Not hidden behind a rank — everybody on a sprint board needs to see the
   * sprint — but behind the board's own setting, because a Sprints entry on a
   * board that runs on flow is a screen that can only ever say "no sprints yet"
   * and an invitation to turn on something nobody chose.
   */
  onlyInSprints?: true;
  /**
   * SHOWN ONLY ON A BOARD RUN AS A PROJECT — #435.
   *
   * The same reasoning as onlyInSprints: a board run as a continuous service has
   * no plan behind it, so a Plan entry could only ever say "no plan" and invite
   * a shape the board was explicitly set not to use. Gated on the board's own
   * deliveryMode (via isProjectMode), not a rank. Absent reads as a project, so
   * every board that predates this keeps the tab.
   */
  onlyInProject?: true;
  /**
   * SHOWN ONLY TO A WORKSPACE ADMIN — #359.
   *
   * Not a rank on the board (`needs` is checked against the previewed board
   * role) but the workspace admin flag itself, because approving who may join
   * the board is a workspace power. A manager cannot decide a request — the
   * invite refuses a non-admin — so showing them the inbox would only be a
   * queue they cannot act on.
   */
  adminOnly?: true;
  /**
   * A NESTED CHILD of another nav row — the sub-menu pattern (R19, Vinnie 3 Oct 2026).
   * A child renders indented beneath its parent, inside the parent's disclosure,
   * and inherits the parent's hue so the sub-tree reads as one family (no accent
   * rail, R12). The parent row becomes expandable; it still navigates to its own
   * screen when its label is clicked. The mechanism is live but currently has NO
   * users: the money screens that first used it moved to their own Finances group
   * (R20, 4 Oct), and Epics — which briefly sat under Board — moved to the
   * Portfolio's Epics lens (placement A, 6 Oct). Kept in place for the next child,
   * one `parent:` entry away.
   */
  parent?: View;
}[] = [
  // MY WORK — the personal, cross-board hub (#457), a lone row pinned at the very
  // top above the board-scoped groups (no group header, like Settings at the
  // foot). For everyone: it answers "what do I have to do" before "what is on
  // this board". The verb is ACT, distinct from Portfolio (organise) and Insights
  // (measure). Board stays the landing for now — this is a prominent top row.
  { view: 'mywork', label: 'My Work', group: 'MyWork' },
  // WORK — the day-to-day: the board, the plan behind it, the timeboxes, the
  // queue. Plan sits beside the board because it answers a different question —
  // which phase are we in and is it on time, not what to pick up next (#377).
  { view: 'board', label: 'Board', group: 'Work' },
  // EPICS lives in the Portfolio now — the "Epics" lens, workspace-wide and
  // cross-board, is a far better home than a board-scoped child here (placement A,
  // 6 Oct: reverses the Epics-under-Board child). Board is a plain row again.
  { view: 'plan', label: 'Plan', group: 'Work', onlyInProject: true },
  // The RAID register — the project's risks, assumptions, issues and dependencies,
  // one screen behind a tab strip. A CHILD of Plan (signed off 20 Oct 2026): it is a
  // planning surface beside the charter and milestones, and it is the single source of
  // truth the charter's risk section and the portfolio read from. Inherits the Plan
  // hue as a child — no own --nav token (see --nav-riskregister, pointed at Plan).
  { view: 'riskregister', label: 'Risk Register', group: 'Work', parent: 'plan', onlyInProject: true },
  // The Learning & Governance spine — 3.19. The decision log and the lessons
  // register sit with the Plan (project-scoped); the library is cross-board, below.
  { view: 'decisions', label: 'Decisions', group: 'Work', onlyInProject: true },
  { view: 'lessons', label: 'Lessons', group: 'Work', onlyInProject: true },
  { view: 'sprints', label: 'Sprints', group: 'Work', onlyInSprints: true },
  { view: 'backlog', label: 'Backlog', group: 'Work' },
  // FINANCES — the money, in its own group placed right after Work (R20, Vinnie
  // 4 Oct 2026). Lifted OUT of Plan: "Plan is really for planning; financials and
  // budgets are completely different." The two money reads sit together as peer
  // rows (not a sub-menu) in the ledger-teal hue (--nav-finances):
  //  · Budget & payments — this project's budget health, the forecast against the
  //    ledger, the capex/opex split and the payments ledger, promoted out of the
  //    old budget modal into a screen of its own. Shown to anyone who can see the
  //    board; editing the budget stays gated inside.
  //  · Ready to Pay — the cross-board queue of approved-not-yet-paid invoices,
  //    with the proof of authority and an export for finance. Reads across boards,
  //    gated manager+ (whoever sees the budgets sees what is due). Placed LAST in the
  //    group (after Payment Approval) so Finances reads in process order (R29).
  // PROJECT MODE ONLY (#435, Vinnie 8 Oct 2026: "anything to do with projects
  // should only be available when run as a project"). A board run as a continuous
  // service has no contract, baseline or forecast — budget and payments are
  // project money, so the whole Finances group is gated on deliveryMode. Existing
  // boards default to 'project', so this hides for no one until a board is set to
  // continuous. The contract value + budget form lives behind this, so it is
  // gated by the same flag without a separate check.
  { view: 'budgetpay', label: 'Budget & Payments', group: 'Finances', onlyInProject: true },
  // WHO AUTHORISES A PAYMENT — the configurable approval chain (executive-approver
  // tier). A workspace-level setup (default chain + per-project overrides) with its
  // own ledger-teal row so it is findable from Finances, not only from Settings.
  // ADMIN-ONLY (manageWorkspace): this sets the workspace-wide default chain and can
  // override any project, so with many project managers it is not a per-PM control —
  // configuring WHO approves payments is a segregation-of-duties matter. (A dedicated
  // finance-admin role + moving this into a reorganised Settings is scoped for 3.20.)
  // Authorising an actual payment is unaffected — that stays with whoever the chain names.
  { view: 'payapproval', label: 'Payment Approval', group: 'Finances', needs: 'manageWorkspace', onlyInProject: true },
  // Ready to Pay — the cross-board queue of approved-not-yet-paid invoices, with the proof
  // of authority and an export for finance. Placed LAST in Finances so the group reads in
  // the real order of the process: Budget & Payments → Payment Approval → Ready to Pay
  // (R29, Vinnie 24 Oct 2026). Reads across boards, gated manager+ (editSettings).
  { view: 'payments', label: 'Ready to Pay', group: 'Finances', needs: 'editSettings', onlyInProject: true },
  // DELIVER — what has been agreed and shipped, rather than what is left to do:
  // releases (#193), the project's documents, and the sign-offs on them.
  //
  // DOCUMENTS SITS BESIDE RELEASES because it answers the same kind of question —
  // what has been agreed and when. It is every document on this project, in one
  // place with dates: what the project was handed, and what it sent to be signed.
  // A project's Plan carries a NARROW summary of it (the received/reference files
  // and the charter, with a line for what has gone for sign-off) and drills in
  // here; but the screen has its own sidebar home, because a team working on the
  // board and sprints — a refactor, maintenance, plain software delivery — has no
  // reason to open the Plan, and must still be able to find, read and make a
  // document. (It was folded onto the Plan for a moment and that orphaned it for
  // exactly those boards; this is the fix.)
  //
  // Sign-off used to live INSIDE Documents, so a team with no documents could not
  // record an approval at all; it is its own screen now, always shown — hiding it
  // when nothing waits left no way to review what HAS been signed off, so it lands
  // on an honest empty state, the badge carrying the count when work waits (#332).
  { view: 'releases', label: 'Releases', group: 'Deliver' },
  { view: 'documents', label: 'Documents', group: 'Deliver' },
  { view: 'approvals', label: 'Sign-Offs', group: 'Deliver' },
  // TEAM — who is doing the work and who may join it. People and access is shown
  // to members too, not just managers (#359): a member cannot manage anyone but
  // may ask an admin to add a colleague (`askToAdd`), and this is where they do
  // it; the controls inside are gated, so the wider door grants nothing new. Its
  // approve/deny queue is a tab within it, badged below — not a separate nav row.
  { view: 'activity', label: 'Activity', group: 'Team' },
  { view: 'dashboard', label: 'Insights', group: 'Team' },
  // Every project on the tenant, one row each — the weekly-meeting roll-up.
  // Beside Insights: Insights reads one board's numbers, the portfolio reads all.
  //
  // MANAGER-AND-ABOVE, because it lays every project's budget, forecast and health
  // on one screen. Nothing here is data a manager could not already reach board by
  // board (the budget ledger shows figures to any member; `projects` is already
  // scoped to boards you can open) — but putting every project's money in one view
  // is a manager's read, not everyone's. `editSettings` is rank 4 (manager), the
  // same capability the Plan and the budget ledger gate on; admin (rank 5) is above
  // it, so admins see it too. The view is gated again at render (defence in depth).
  { view: 'portfolio', label: 'Portfolio', group: 'Team', needs: 'editSettings' },
  // The workspace-wide lessons library — cross-board, so it reads like the
  // Portfolio and sits in the Team group, not gated to a project.
  { view: 'lessonslibrary', label: 'Lessons Library', group: 'Team' },
  { view: 'admin', label: 'Workspace', group: 'Team', needs: 'askToAdd' },
  // On its own at the foot — not part of the daily lists.
  { view: 'settings', label: 'Settings', group: 'Settings' },
  /*
   * RUNNING THE BUSINESS IS NOT ON THIS MENU, and there is no hidden route to
   * it either. It was here briefly, shown only to staff — but hiding an entry
   * hides the BUTTON, not the code: the screen, its wording and the names of
   * the endpoints it calls still went into every customer's browser.
   *
   * It now lives at its own address, built separately, so a customer downloads
   * none of it. See src/admin/ — and scripts/test-nothing-operator-ships.mjs,
   * which reads the built product bundle and fails if any of it comes back.
   */
];

/*
 * WHAT A VENDOR SEES IN THE SIDEBAR — Feature B, written out by hand on purpose.
 *
 * A vendor is a supplier doing the work on one board, so the sidebar carries the
 * work surface and nothing around it: their cross-board hub, the board, the queue
 * behind it, and the documents shared with them. Everything else — the plan, the
 * decision log, the money, releases, sign-offs, insights, the portfolio, the
 * workspace and settings — is ABSENT, not greyed (B-01): a vendor never meets a
 * control they cannot use.
 *
 * It is an ALLOW-LIST, not a block-list, for the same reason the capability matrix
 * in permissions.ts is: a screen added later does not reach a vendor unless somebody
 * writes its view in here too. And it is only the menu — the security is server-side
 * (migration 0196): hitting one of these screens by a saved link or the API is
 * refused by the row policies, not merely hidden here.
 */
const VENDOR_VIEWS = new Set<View>(['mywork', 'board', 'backlog', 'documents']);

export default function App() {
  const view = useApp((s) => s.view);
  const setView = useApp((s) => s.setView);
  const openIssue = useApp((s) => s.openIssue);
  const setProject = useApp((s) => s.setProject);
  const search = useApp((s) => s.search);
  const setSearch = useApp((s) => s.setSearch);
  const startCreate = useApp((s) => s.startCreate);
  const setupForced = useApp((s) => s.setupForced);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // The palette opens two ways: ⌘K in its full 'all' mode, the Help row in 'help'
  // mode (the same surface, "how do I…"). One flag for which door was used.
  const [paletteMode, setPaletteMode] = useState<'all' | 'help'>('all');
  // FIRST-RUN SIGNPOST that the help exists (Vinnie, 2 Oct: "you don't know a
  // help feature is there unless you're told"). A one-time coach-mark points at
  // the Help row; shown until dismissed (×, Esc, or opening Help), then never
  // again on this browser. Design-lead mock, Vinnie's pick (Option 2 + 1).
  const [helpIntro, setHelpIntro] = useState(() => {
    try {
      return localStorage.getItem('flightdeck-help-intro-seen') !== 'yes';
    } catch {
      return false;
    }
  });
  const dismissIntro = () => {
    setHelpIntro(false);
    try {
      localStorage.setItem('flightdeck-help-intro-seen', 'yes');
    } catch {
      /* private window — nothing to persist to */
    }
  };
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  /* Which nav parents have their sub-menu opened BY HAND (the disclosure chevron).
     A parent is also open whenever the current view is it or one of its children,
     so navigating to Plan reveals Budget & payments without a second click; this
     set only holds the extra ones a person opened while elsewhere. Not persisted —
     the sub-menu is light, and following the active view is the common case. */
  // A sub-menu FOLLOWS WHERE YOU ARE: open when you are on its parent or one of
  // its children, closed when you are not (Vinnie, 3 Oct: "when on the screen it
  // opens, when not on the screen it closes"). On top of that a person may work
  // the chevron by hand — open a menu they are NOT on to step straight into a
  // child, or fold away one they are on. That hand-set intent is a one-off: it
  // holds until the next navigation, then the where-you-are rule takes back over.
  // So the map (parent → hand-set open/closed) is cleared whenever the view
  // changes, and `subOpen` below falls back to the automatic rule.
  const [expandedNav, setExpandedNav] = useState<Map<View, boolean>>(new Map());
  useEffect(() => {
    setExpandedNav((prev) => (prev.size ? new Map() : prev));
  }, [view]);
  const toggleNavParent = (v: View, openNow: boolean) =>
    setExpandedNav((prev) => {
      const next = new Map(prev);
      next.set(v, !openNow);
      return next;
    });
  const coach = useApp((s) => s.coach);
  const showCoach = useApp((s) => s.showCoach);

  /* Which sidebar groups are folded away, kept per-browser (src/nav-groups.ts).
     The group holding the current view is never hidden — that rule is applied
     where the nav renders, so folding the group you are in simply takes effect
     once you leave it. */
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(readCollapsedGroups);
  const toggleGroup = (g: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(g) ? next.delete(g) : next.add(g);
      writeCollapsedGroups(next);
      return next;
    });
  /* Folding is a SIDEBAR idea. On a phone the sidebar is a scrolling row of tabs
     with no group headings, so a folded group must not drop its tabs — folding is
     desktop-only. Tracked live so it is right after a rotate or a window resize. */
  const [isPhone, setIsPhone] = useState(
    // Guarded: matchMedia is absent under server-side render (the screen tests)
    // and in older engines, where reading it would throw during the first render.
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 720px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 720px)');
    const on = () => setIsPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  /*
   * ⌘K, or Ctrl K.
   *
   * On the window, because it has to work wherever you are — including from
   * inside the search box, which is the one place somebody would try it first.
   * preventDefault matters: the browser has its own ⌘K in the address bar.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteMode('all');
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* Escape dismisses the help coach-mark — only while one is up, so it never
     swallows Escape from anything else. */
  useEffect(() => {
    if (!coach) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') showCoach(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [coach, showCoach]);

  /* Escape also dismisses the one-time first-run help pointer. */
  useEffect(() => {
    if (!helpIntro) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissIntro();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [helpIntro]);

  /* GENERIC REVEAL — a help answer drills to a screen and the control it names
     wears `data-reveal="<id>"`; after the view mounts we flash it (`.just-revealed`,
     which the coach-mark then rings and anchors to). One place, so a new anchor is
     just an attribute on the control plus the matching `reveal` on its how-to —
     no per-screen wiring. Screens with their own revealId handling (Plan's About,
     a Documents row) use ids we never tag here, so this no-ops for them. */
  const revealId = useApp((s) => s.revealId);
  // Re-run on every drill, not only when the id changes, so a second "Take me
  // there" to a control already on screen flashes it again rather than no-opping
  // (L99). `revealSeq` bumps on each drillTo; depending on it is what makes the
  // repeat a state change.
  const revealSeq = useApp((s) => s.revealSeq);
  useEffect(() => {
    if (!revealId) return;
    let timer = 0;
    let clearFlash = 0;
    let tries = 0;
    // A data-loaded screen (Portfolio) mounts its control AFTER its data
    // resolves — later than a single 90ms query — so seek a few times before
    // giving up, then tell the coach-mark its target has arrived so it anchors
    // instead of being stuck in top-of-view (ux-witness, 2 Oct). Only fires for
    // real `[data-reveal]` ids, so screens with bespoke reveals are untouched.
    const seek = () => {
      const el = document.querySelector(`[data-reveal="${revealId}"]`);
      if (el) {
        el.scrollIntoView({ block: 'center' });
        el.classList.add('just-revealed');
        clearFlash = window.setTimeout(() => el.classList.remove('just-revealed'), 1800);
        window.dispatchEvent(new CustomEvent('coach:anchor'));
        return;
      }
      if (++tries <= 10) timer = window.setTimeout(seek, 130);
    };
    timer = window.setTimeout(seek, 90);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clearFlash);
    };
  }, [revealId, view, revealSeq]);
  /* A held coach-mark must never sit over the ⌘K palette (it is drawn above it
     and would swallow the top result). Opening the palette dismisses any coach,
     so the "ask another question" loop starts clean and one Esc closes the
     palette, not the card behind it (ux-witness, 2 Oct). */
  useEffect(() => {
    if (paletteOpen) showCoach(null);
  }, [paletteOpen, showCoach]);
  const source = useApp((s) => (s.meta.source ?? 'tracker') as IssueSource);
  const sourceName = useApp((s) => s.meta.sourceName?.trim() || DEFAULT_SOURCE_NAME);
  const selectedId = useApp((s) => s.selectedId);
  const creating = useApp((s) => s.creating);
  const startingBoard = useApp((s) => s.startingBoard);
  const setStartingBoard = useApp((s) => s.setStartingBoard);
  const user = useApp((s) => s.user);
  const notifications = useApp((s) => s.notifications);
  const authReady = useApp((s) => s.authReady);
  const emailVerified = useApp((s) => s.emailVerified);
  const denied = useApp((s) => s.denied);
  /* `forced` is a test-copy-only view of the screen — see env.ts. It can never
     be true on live, whatever the address says. */
  const stale = useApp((s) => s.stale) || forced('stale');
  /* Forced too, because a real one needs a customer to have been switched off
     and cannot be arranged on a test copy — see forced() in env.ts. */
  const suspendedReal = useApp((s) => s.suspended);
  const suspended = forced('suspended')
    ? { name: 'Acme Insurance', why: 'Invoice unpaid since 1 July. Ring the account manager on 555-0142.' }
    : suspendedReal;
  const loaded = useApp((s) => s.loaded);
  const signOutUser = useApp((s) => s.signOutUser);
  const role = useApp((s) => s.shownRole);
  // On a read-only sample board the edit controls stay visible but inert, and a
  // change attempt gets a gentle note (mock 03) — see SampleStrip/useSampleBoard.
  // The treatment is for the sample's OWN screens: on a cross-board or workspace
  // view (My work, Portfolio, …) the selected board may be a sample, but the
  // screen is not showing it, so the strip and the inert New-issue stay off.
  const sample = useSampleBoard();
  const onSampleScreen = sample.readOnly && !CROSS_BOARD.has(view);
  /* The WORKSPACE rank, which is what People-and-access is scoped to. It picks
     the full management screen (manager or admin) apart from the read-only
     member view — #359. `role` above is only this board's. */
  const workspaceRole = useApp((s) => s.role);
  const issues = useApp((s) => s.issues);
  // The Activity feed (and this badge) also carries the sign-off lifecycle —
  // documents sent for sign-off, and every approval/rejection — so the same
  // state it reads has to reach the count, or the badge and the list drift.
  const documents = useApp((s) => s.documents);
  const signoffs = useApp((s) => s.signoffs);
  const sprints = useApp((s) => s.sprints);
  const builds = useApp((s) => s.builds);
  const lastSeen = useApp((s) => s.lastSeen);
  const profiles = useApp((s) => s.profiles);
  const profilesOk = useApp((s) => s.profilesOk);
  const projects = useApp((s) => s.projects);
  const projectsKnown = useApp((s) => s.projectsKnown);
  const projectId = useApp((s) => s.projectId);
  // Absent is flow — see ProjectMeta.cadence. Read here rather than inside
  // the Sprints screen because it decides whether the screen is offered at all.
  const inSprints = useApp((s) => s.meta.cadence) === 'sprints';
  // Absent is a project — see ProjectMeta.deliveryMode and isProjectMode. Read
  // here, once, so the Plan tab, the palette and the view guard all agree.
  const isProject = useApp((s) => isProjectMode(s.meta));
  const viewing = useApp((s) => s.viewing);
  const stopPreview = useApp((s) => s.stopPreview);
  const saveProblem = useApp((s) => s.saveProblem);
  const clearSaveProblem = useApp((s) => s.clearSaveProblem);
  const flash = useApp((s) => s.flash);
  const showFlash = useApp((s) => s.showFlash);
  // A confirmation toast (from the command palette) clears itself after a beat.
  // Keyed on the message so a second command while the first is still up resets
  // the timer rather than letting the new one vanish early.
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => showFlash(null), 2200);
    return () => clearTimeout(t);
  }, [flash, showFlash]);

  /*
   * ACT ON A /?card= LINK, ONCE THE SESSION AND THE BOARD LIST ARE IN — #413.
   *
   * Held until sign-in has resolved and the boards this person can see are
   * known, because both decisions depend on them: an unauthenticated arrival
   * sees the sign-in card with the query still on the address, and the card is
   * only opened on a board they actually belong to. Fired once — a module flag,
   * not a re-run on every board switch, since opening the card itself changes
   * the state this watches. The address is tidied afterwards so a reload does
   * not reopen the card and the ids do not linger where they can be shared.
   */
  useEffect(() => {
    if (cardLinkFired || !cardLink) return;
    if (!authReady || !user || !projectsKnown) return;
    cardLinkFired = true;
    const onThisBoard = !cardLink.board || projectId === cardLink.board;
    const canReach = !cardLink.board || projects.some((p) => p.id === cardLink.board);
    if (canReach) {
      if (!onThisBoard) setProject(cardLink.board);
      setView('board');
      openIssue(cardLink.card);
    }
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('card');
      u.searchParams.delete('board');
      window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
    } catch {
      /* A tidied address is a courtesy, not a requirement — never block on it. */
    }
  }, [authReady, user, projectsKnown, projects, projectId, setProject, setView, openIssue]);
  // Project-only destinations (NAV items flagged onlyInProject — the Plan, the
  // Finances group) have no meaning on a continuous board. If we are on one — a
  // stale link, or the mode was just switched to continuous while the screen was
  // open — fall back to the board rather than show an empty screen. The tab and
  // the ⌘K palette already hide them (navVisible); this closes the direct-nav
  // path for all of them at once.
  useEffect(() => {
    if (isProject) return;
    if (NAV.some((n) => n.onlyInProject && n.view === view)) setView('board');
  }, [isProject, view, setView]);
  // A vendor sees only the work surface (VENDOR_VIEWS). The sidebar and palette
  // already hide everything else; this closes the direct-nav path — a stale link
  // or a view remembered from before the role was granted lands on the board, not
  // an empty or forbidden screen. (The data is refused server-side regardless, 0196.)
  useEffect(() => {
    if (role !== 'vendor') return;
    if (!VENDOR_VIEWS.has(view)) setView('board');
  }, [role, view, setView]);
  const myEmail = user?.email ?? '';
  // Only other people's activity is "news" — your own changes are not.
  // Memoised because this walks every issue and every entry on it, and without
  // it the whole feed was rebuilt and re-sorted on each keystroke in the search box.
  const unread = useMemo(
    () =>
      buildFeed(issues, documents, signoffs, sprints, builds).filter(
        // `e.who &&` matches Activity's isNew: an actorless "sent for sign-off"
        // milestone is shown but never counted, so the badge cannot read a number
        // the list has no bold rows to explain.
        (e) => e.who && e.at > lastSeen && e.who !== myEmail
      ).length,
    [issues, documents, signoffs, sprints, builds, lastSeen, myEmail]
  );

  /* The "My work" nav badge — the unread call-outs waiting on an ACTION from me,
     across every board (sign-offs asked of me, mentions, covers handed to me).
     Read from the one `inboxActionCount` rule the Inbox tab uses, so the badge
     and the tab's own count can never disagree. Cross-board, so it reads the
     user's notification feed (store.notifications), not this board's activity. */
  const inboxWaiting = useMemo(() => inboxActionCount(notifications), [notifications]);

  /*
   * THE TWO REQUEST BADGES — #359, and never both at once for one person.
   *
   * The same nav badge the activity feed uses, so "something needs you here"
   * reads the same wherever it is. Which side of the feature you are on decides
   * which one you see, exactly as the screens themselves divide:
   *   · an admin → what is WAITING on the Requests inbox, the requests still
   *     to decide (`requestsPending`);
   *   · everyone else → their own asks an admin has answered and they have not
   *     cleared yet, a person or an assistant alike, on People and access
   *     (`myOutcomes`). An admin never sees this one — they act in Requests.
   * Fulfilled ones are done and count for nobody. Both are computed always so
   * the hooks are unconditional; the render picks the one that belongs to you.
   */
  const boardRequests = useApp((s) => s.boardRequests);
  const requestsCleared = useApp((s) => s.requestsCleared);
  const requestsPending = useMemo(
    () => boardRequests.filter((r) => r.status === 'pending').length,
    [boardRequests]
  );
  /* How many things are waiting on a signature — badges the Sign-offs nav item
     and, being above zero, is what makes it appear at all. `signoffs`, `builds`,
     `documents` and `sprints` are read once at the top (they feed the Activity
     badge too), so they are not re-declared here. */
  const meta = useApp((s) => s.meta);
  /*
   * WHAT THE SIGN-OFFS BADGE COUNTS. The sent-but-unsigned sheets, and — on a
   * slots board — the deliverables change requests drafted for a finished sprint
   * and not yet sent. A draft has no signatures, so without the second term a
   * finished sprint would stop counting the moment its document was created,
   * which is the opposite of "the reminder persists". Read from the same helper
   * the Sign-offs inbox uses, so the badge and the list always agree — including
   * the exclusion below: a withdrawn or rolled-back release is out, so the inbox
   * drops it and the count must too, or the badge would sit above an inbox that
   * no longer lists what it is counting.
   */
  const signoffsWaiting = useMemo(() => {
    const pulledBuilds = new Set(builds.filter((b) => b.pulled).map((b) => b.build));
    const live = signoffs.filter((r) => r.build == null || !pulledBuilds.has(r.build));
    const sent = outstandingSignoffCount(live);
    const prepared = usesSlots(readPolicy(meta.signoffPolicy))
      ? preparedSprintSignoffs(documents, sprints).length
      : 0;
    return sent + prepared;
  }, [signoffs, builds, documents, sprints, meta]);
  const myOutcomes = useMemo(() => {
    const mine = myEmail.toLowerCase();
    return boardRequests.filter(
      (r) =>
        r.requestedBy === mine &&
        r.status !== 'pending' &&
        r.status !== 'fulfilled' &&
        !requestsCleared[r.id]
    ).length;
  }, [boardRequests, requestsCleared, myEmail]);

  /*
   * WHERE THIS PERSON CAN ACTUALLY GO — one visibility rule, so the sidebar and
   * the command palette can never disagree. The palette used to map every NAV
   * entry unfiltered, so it would jump to Sprints on a board that runs in flow,
   * or to People and access / Admin without the rank for them — screens the
   * sidebar deliberately hides. Both read this now: a destination is reachable
   * from one exactly when it is from the other.
   */
  const navVisible = (n: (typeof NAV)[number]): boolean =>
    // A vendor is scoped to the work surface — the allow-list above is the whole
    // of their sidebar, and the palette reads the same rule (B-01).
    (role !== 'vendor' || VENDOR_VIEWS.has(n.view)) &&
    (!n.needs || can(role, n.needs)) &&
    (!n.onlyInSprints || inSprints) &&
    (!n.onlyInProject || isProject) &&
    (!n.adminOnly || can(workspaceRole, 'manageWorkspace'));

  /*
   * WHICH COMPANIES THIS PERSON IS IN, for the gate below — #218.
   *
   * `null` until it is known, and the gate waits for that rather than treating
   * "not loaded yet" as "not a member". Without the distinction, everybody sees
   * the refusal page for an instant on the way in, which is a worse fault than
   * the one being fixed.
   *
   * Only asked when the address actually names a company. Almost nobody's does
   * — every bookmark and every emailed link carries none — so this is one
   * request that most sessions never make.
   */
  const askedFor = companyInAddress();
  const [companies, setCompanies] = useState<
    { id: string; name: string; slug: string; suspended: boolean }[] | null
  >(null);
  /**
   * ⚠️ THE READ FAILED, WHICH IS NOT THE SAME AS "YOU ARE IN NONE" — #334.
   *
   * This used to be `.catch(() => setCompanies([]))`, with a comment saying
   * "unreadable is not the same as empty, but the person is still stuck and an
   * empty list gives them the page with a way out rather than a spinner". The
   * instinct not to hang was right; the conclusion was wrong. An empty list,
   * with a company in the address, does not produce a way out — it produces the
   * screen below saying **"You are not a member of X"** about a workspace the
   * person owns, with everything still in it and nothing to try.
   *
   * It was found three times in one evening on the test copy: a brand-new
   * workspace turning away its own administrator, which went away after
   * deleting unrelated workspaces — fewer rows, and the read stopped failing.
   * On a customer's bad connection it needs no fixtures at all.
   *
   * IT IS #324 ONE GATE FURTHER OUT. That was a membership list built with
   * `is_assistant = false`, so an assistant was absent and absence was read as
   * "not approved". This is absence for a different reason — the answer never
   * arrived — being read the same way. Both turn *I do not know* into *no*.
   *
   * So there are three states now, and they are kept apart: not asked yet
   * (null, and the app waits), asked (a list, however short), and could not ask.
   */
  const [couldNotAsk, setCouldNotAsk] = useState(false);
  /** Bumped by Try again, which is the whole point of knowing the difference. */
  const [askAgain, setAskAgain] = useState(0);
  useEffect(() => {
    /*
     * NOT ONLY WHEN THE ADDRESS NAMES ONE — widened for #247. The list used to
     * be fetched just to answer "is this company mine?"; it now also answers
     * "which company, of the several you are in?", and that question is asked
     * precisely when the address names none.
     */
    if (!user || !emailVerified) return;
    let stop = false;
    setCouldNotAsk(false);
    /*
     * A BLIP IS NOT AN ANSWER, SO ASK AGAIN BEFORE BELIEVING IT — the read is
     * fast and reliable when it lands (≈100ms), so the "could not ask" screen
     * seen on a fresh sign-in is a single dropped request — a cold connection,
     * a momentary 5xx — not a real refusal. #334 rightly keeps "could not ask"
     * apart from "you are in none"; this keeps one lost request from reaching
     * that screen in the first place. Three tries over about a second, backing
     * off, and only then does the honest error stand. `stop` (unmount, or a
     * re-run from Try again) ends the loop at once.
     */
    (async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          const list = await accounts.workspaces();
          if (!stop) setCompanies(list);
          return;
        } catch {
          if (stop) return;
          /* Recorded as "could not ask" and NEVER as an empty answer, but only
             once the retries are spent. Nothing below may claim to know
             something this did not find out — see #334 above. */
          if (attempt >= 2) {
            setCompanies(null);
            setCouldNotAsk(true);
            return;
          }
          await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
        }
      }
    })();
    return () => {
      stop = true;
    };
  }, [user, emailVerified, askAgain]);

  if (!authReady)
    return (
      <>
        <EnvBanner />
        <div className="center-note">Loading…</div>
      </>
    );
  if (!user)
    return (
      <>
        <EnvBanner />
        <Login />
      </>
    );
  if (!emailVerified)
    return (
      <>
        <EnvBanner />
        <VerifyEmail />
      </>
    );
  /*
   * BEFORE `stale`, and for the same kind of reason it sits before `denied`:
   * all three arrive here as the same nothing, and only one of them is true.
   *
   * A SUSPENDED WORKSPACE IS NOT A BROKEN SIGN-IN. Telling somebody to sign in
   * again when the reason they cannot get in is an unpaid invoice sends them
   * round a loop that cannot end, and it is the person least able to work out
   * why. The reason was typed by whoever suspended them, and it is shown
   * exactly as typed — see 0028. There is nothing to press, deliberately: the
   * way out of this is a conversation, not a button.
   */
  if (suspended)
    return (
      <div className="center-note">
        <p>
          <strong>{suspended.name}</strong> has been suspended.
        </p>
        {suspended.why && <div className="login-error">{suspended.why}</div>}
        <p className="hint">
          Nothing has been deleted. Everything is exactly where you left it and comes back the
          moment the account is turned back on.
        </p>
        <button className="btn" onClick={signOutUser}>
          Sign out
        </button>
      </div>
    );
  /*
   * BEFORE `denied`, because this person IS a member and being told otherwise
   * is the wrong sentence — see the note in store.ts. The app has already tried
   * to fix it without asking; reaching here means that did not work, so the one
   * thing left is a fresh sign-in.
   */
  if (stale)
    return (
      <div className="center-note">
        <p>
          <strong>{user.email}</strong> needs to sign in again.
        </p>
        <p className="hint">
          Your sign-in was issued before the board last changed and can no longer reach your
          workspace. Signing in again fixes it. Nothing has been lost.
        </p>
        <button className="btn btn-primary" onClick={signOutUser}>
          Sign in again
        </button>
      </div>
    );
  /*
   * ── AN ACCOUNT THAT ADMINISTERS FLIGHTDECK IS NOT A LOST CUSTOMER ────────
   *
   * BEFORE `denied`, because that screen offers Start your own workspace — and
   * an operations account has no workspace, so it lands there and is invited to
   * make one. Vinnie hit this on live on 17 August: "I am stuck on the who are
   * you window because I tried to log in as ops@." Stuck was the lucky outcome.
   * Filling that form in would have created a workspace on LIVE owned by the
   * account that administers every other one — which is the precise thing the
   * portal refuses to let anybody do deliberately, arrived at by accident from
   * the other side.
   *
   * THE MIRROR OF A GUARD THAT ALREADY EXISTED, and only ever existed one way
   * round: the portal turns away accounts that are not staff, and the product
   * did nothing about accounts that are. Half a rule enforced in one direction
   * is how a thing everybody agrees must never happen happens.
   *
   * ONLY WHEN `denied`. A staff account on a support visit HAS access, so it
   * never reaches here and its visit is unaffected — this catches the one with
   * no way into anything, which is what an operations account normally is.
   *
   * THE ADDRESS IS DELIBERATELY NOT WRITTEN HERE. Every customer downloads this
   * bundle, and the only person who can ever read this sentence already knows
   * where the portal is. Naming it would put a breadcrumb in front of everybody
   * else to tell one person something they know.
   *
   * `amStaff` reads local storage, so anybody can make this sentence appear.
   * That is fine: it grants nothing and takes nothing away. It replaces an
   * invitation with an explanation, and the database is what decides the rest.
   */
  if (denied && amStaff())
    return (
      <div className="center-note">
        <p>
          <strong>{user.email}</strong> administers FlightDeck.
        </p>
        <p className="hint">
          It is not a member of any workspace here, and it should not become one — this is the
          product customers use. Sign out and use the operations portal instead.
        </p>
        <button className="btn btn-primary" onClick={signOutUser}>
          Sign out
        </button>
      </div>
    );
  if (denied)
    return (
      <div className="center-note">
        <p>
          <strong>{user.email}</strong> is signed in but not approved for this board.
        </p>
        {/* This named one person. Anybody refused entry to anybody else's board
            was told to ask a stranger. */}
        <p className="hint">Ask an administrator of this board to add your address.</p>
        {/* The other kind of person who lands here: somebody who came to try
            it, and was being told to go and ask a stranger for permission to
            use a thing they had just signed up for. */}
        <StartWorkspace />
        <button className="btn" onClick={signOutUser}>
          Sign out
        </button>
      </div>
    );
  /*
   * THE ADDRESS NAMES A COMPANY THIS PERSON CANNOT OPEN — #218.
   *
   * AFTER the plain broken states above, and before the board. `suspended`,
   * `stale` and `denied` are all about the account itself and each has one true
   * sentence; this is about the LINK somebody just followed, which is a working
   * account in an ordinary situation.
   *
   * WHY IT HAS TO BE SAID AT ALL. `my_tenant()` does the safe thing on its own:
   * it ignores a company you are not in and falls back to your own. Without
   * this gate that is silently correct and visibly a lie — the address bar says
   * TRINRE and the board is FlightDeck's, which is the exact fault #218 exists
   * to end. So when the two disagree, the page says so.
   */
  /*
   * COULD NOT ASK — #334, and it comes BEFORE the not-yours gate below on
   * purpose. That gate is the one that used to fire on this state and accuse
   * somebody of not belonging to their own workspace.
   *
   * It says what happened and offers the thing that fixes it nine times in ten,
   * which is asking again. Signing out is kept beside it because somebody who
   * really is on the wrong account needs a way off this screen too.
   */
  if (couldNotAsk)
    return (
      <>
        <EnvBanner />
        <div className="center-note">
          <p>
            <strong>Could not check which companies you are in.</strong>
          </p>
          {/* Says which way round it is. "You are not a member" was the old
              wording for this state, and it was an accusation rather than a
              fact — nothing here has found out anything about your access. */}
          <p className="hint">
            This is the connection, not your account. Nothing has been changed and nothing is
            missing; the answer simply did not arrive.
          </p>
          <button className="btn btn-primary" onClick={() => setAskAgain((n) => n + 1)}>
            Try again
          </button>
          <button className="btn" onClick={signOutUser}>
            Sign out
          </button>
        </div>
      </>
    );

  if (askedFor && companies !== null && !companies.some((w) => w.slug === askedFor))
    return (
      <>
        <EnvBanner />
        <CompanyNotYours
          slug={askedFor}
          me={user.email ?? ''}
          mine={companies}
          onLeave={(slug) => {
            // A reload rather than a re-render: moving company changes the
            // answer to every question the app has already asked, and the
            // switcher takes the same route for the same reason.
            window.location.assign(addressFor(slug));
          }}
          onSignOut={signOutUser}
        />
      </>
    );
  /*
   * THE ADDRESS NAMES NO COMPANY AND THERE IS MORE THAN ONE — #247.
   *
   * AFTER the not-yours gate above, because that one is about a link somebody
   * followed and this is about there being no link at all. BEFORE `loaded`,
   * and that ordering is the whole point: `my_tenant()` decides what every
   * query returns, so a board fetched first would be fetched as the wrong
   * company and then relabelled.
   *
   * WHAT COUNTS AS ALREADY ANSWERED IS THE ADDRESS, AND ONLY THE ADDRESS.
   * Not what the sign-in token remembers: it holds whichever company was last
   * picked, on any day, in any browser, and is never cleared, so consulting it
   * meant nobody who had ever used the switcher was asked again. That is how
   * this shipped correct and invisible — see whichCompany() for what Vinnie
   * found on 24 August 2026. Choosing reloads at the company's own address, so
   * one answer is all anybody is asked for. Somebody in one company never sees
   * this at all.
   */
  const decided = companies ? whichCompany(askedFor ?? '', companies) : null;
  if (decided === 'ask' && companies)
    return (
      <>
        <EnvBanner />
        <WhichCompany
          me={user.email ?? ''}
          mine={companies}
          onChoose={async (w) => {
            await accounts.chooseWorkspace(w.id);
            /*
             * A reload to the company's own address, rather than a re-render.
             * Moving company changes the answer to every question the app has
             * already asked — the switcher takes the same route for the same
             * reason — and going via the address means the choice is in the
             * link, so a bookmark made here never asks again.
             */
            window.location.assign(addressFor(w.slug));
          }}
        />
      </>
    );
  if (!loaded) return <div className="center-note">Loading the board…</div>;

  /*
   * THE TERMS, BEFORE THE BOARD — and this `return` is why it works.
   *
   * Not rendered over the top of the app: the app is not rendered at all. A
   * dialog laid over a board is a board that was drawn, and anything drawn can
   * be read — from a screenshot, from the page's own text, from a moment before
   * the overlay paints. Somebody who has not agreed must not have seen it.
   *
   * After `denied` on purpose, so a stranger with no access is turned away
   * without first being shown terms that were never going to apply to them.
   */
  /*
   * A SUPPORT VISIT PASSES BOTH GATES BELOW, AND MUST.
   *
   * Both read the person's row in THIS workspace — their agreed version, and
   * whether they have chosen a password. A staff account visiting a customer
   * has no row here at all, deliberately: a row would put them in the assignee
   * list and the mentions of a company they do not work for.
   *
   * So both gates saw "not agreed" and "no password" and stopped them at the
   * door — and neither could ever be satisfied, because agreeing writes to the
   * row that does not exist. It was a closed loop: every customer workspace
   * demanded terms that could not be accepted. Found by clicking into one.
   *
   * Neither gate is being waived. The staff account agreed to the terms and set
   * a password in its OWN workspace, where its row is, and that is the account
   * these things are properties of. What it is not is a member of this company,
   * and the customer's copy of the terms is an agreement between that company
   * and the people who work there.
   */
  const me = (user.email ?? '').toLowerCase();
  /*
   * THE STAFF ACCOUNT IS NEVER ASKED, WHEREVER IT IS STANDING.
   *
   * The first version of this asked "is staff AND has no row here", which read
   * as the more careful rule and was the one that let the fault through.
   * Agreeing to the terms WRITES the row — so a staff visitor was shown the
   * terms, agreed, and by agreeing became a member of that customer's
   * workspace: in their People list, their assignee picker and their mentions,
   * as a viewer, permanently. Then the password gate fired, because the row it
   * had just made had no password against it. That is what Vinnie hit.
   *
   * So the condition is the mark alone. The staff account agreed to the terms
   * and had a password set when it was made, in its own workspace, and those
   * are properties of the ACCOUNT. A customer's copy of the terms is an
   * agreement between that company and the people who work there, and this
   * account is not one of them — which is the same reason it must not be
   * written into their people table.
   */
  const staffHere = amStaff();

  /*
   * ASKED FIRST, ANSWERED SECOND — #252.
   *
   * Each of the three gates below keeps its own test exactly as it was written;
   * what is new is that none of them may speak before the question has been
   * put. `loaded` stopped being that assurance on 23 August, when the board
   * began painting from the copy the browser kept: that copy holds the cards
   * and nothing about the PERSON, so the screen was drawn while their details
   * and their projects were still in flight, and an empty answer is exactly
   * what "has not agreed" and "has no projects" look like. The terms and the
   * setup wizard were therefore shown on every single refresh to people who
   * had done both, years of trust spent on a fault that was never real.
   *
   * WHY THIS DOES NOT LET ANYBODY PAST. Absent still means ask. A person is
   * only handed a kept copy of a board they themselves saved — recall() checks
   * the address on it — so anybody who has one was already past both gates
   * when it was written. Somebody genuinely new has no copy, waits at
   * "Loading the board…", and meets the terms exactly as before. Nothing about
   * either answer is stored in the browser, so nothing about either becomes
   * something a person can edit.
   *
   * AND IT COSTS NOTHING. Holding the whole screen until the details arrived
   * would trade the instant board for the flash. Holding only the gates does
   * not: the board paints from the kept copy as it does today. Vinnie, 25
   * August 2026: *"no other app for example Amazon puts you through that when
   * you refresh their page, so it can be done."*
   */
  if (shouldShowEula({ staff: staffHere, profilesOk, acceptedVersion: profiles[me]?.eulaVersion, email: me })) {
    return (
      <>
        <EnvBanner />
        <Eula />
      </>
    );
  }

  /*
   * AND A PASSWORD OF THEIR OWN, before the board.
   *
   * AFTER the terms, because agreeing comes before anything is set up, and
   * before everything else for the same reason the terms are: somebody invited
   * arrives holding a link out of their inbox, and a link in an inbox is a key
   * anybody who can read that inbox also holds. Until they have chosen a
   * password, that is all there is between an old forwarded email and their
   * account.
   *
   * Everybody is asked once, including people who were here before this
   * existed — none of them chose a password through the flow that records it,
   * and being asked once is the right outcome for them too.
   *
   * ── EVERYBODY EXCEPT AN ASSISTANT, AND THAT IS #304 ──────────────────────
   *
   * Every word above is about a person: an inbox, a link out of it, a choice
   * only they make. An assistant has none of those. It was handed its password
   * once, by the admin who made it, and that password IS its password — there
   * is nothing for it to replace and nobody to do the replacing.
   *
   * So this gate had nothing to protect it from and one thing it could do to
   * it: shut the door. `claude-fdd@flightdeck.build` — the account that writes
   * to the FlightDeck board — held a working password and `password_set_at`
   * empty, and could not reach the board through the app at all. Nothing on
   * screen said why, because from the app's side nothing was wrong.
   *
   * ⚠️ AND WE COULD NOT SEE IT, WHICH IS THE PART WORTH REMEMBERING. Our own
   * assistant reaches the database through `push-tracker`, which signs in
   * through Supabase Auth directly and never meets a screen. Ours worked. A
   * customer's assistant, following the Assistants tab on their own machine,
   * meets this line and stops.
   *
   * THE INVITE FUNCTION ALREADY STAMPS `password_set_at` FOR AN ASSISTANT, and
   * that is not enough on its own: it fixes accounts made after it shipped and
   * says nothing about the ones made before, and it leaves two facts that have
   * to agree forever. `is_assistant` is the fact. Reading it here means they
   * cannot disagree.
   */
  /*
   * DEACTIVATED — a reversible pause, held out of the board in the same place the
   * password gate holds a new arrival (0208). The person keeps every role and
   * board; they simply cannot come in until an admin turns them back on, so, like
   * the suspended-workspace screen above, there is nothing to press but Sign out —
   * the way back is an admin reactivating them, not a button here. Decided from
   * the people row the booted session already has, beside mustChoosePassword, so
   * both gates read the one source.
   */
  if (amSuspended(profiles[me], { staff: staffHere, profilesOk })) {
    return (
      <div className="center-note">
        <EnvBanner />
        <p>
          <strong>Your access is paused</strong>
        </p>
        <p className="hint">
          An administrator has deactivated your account, so you cannot sign in just now. Nothing has
          been deleted — every board and role is exactly where you left it and comes back the moment
          they turn your account back on.
        </p>
        <button className="btn" onClick={signOutUser}>
          Sign out
        </button>
      </div>
    );
  }

  if (mustChoosePassword(profiles[me], { staff: staffHere, profilesOk })) {
    return (
      <>
        <EnvBanner />
        <ChoosePassword />
      </>
    );
  }

  /*
   * NOTHING TO PUT A SHELL AROUND — so there is no shell.
   *
   * A workspace with no boards was showing the whole app around a setup
   * wizard: a sidebar, a theme picker, a link to People and access, and an
   * account panel. Every one of those is furniture for a board, and there is
   * no board. The navigation had already been stripped to a single item, which
   * was the clue that none of it belonged.
   *
   * Returned BEFORE the shell, exactly like the terms above, and for the same
   * reason: this is a state the app has not been entered from yet, and drawing
   * the app around it says otherwise. Signing in, agreeing to the terms and
   * setting up are three gates in a row, and each is the whole screen while it
   * lasts.
   *
   * WHAT IS STILL MISSING is the half in FRONT of this: registering a company
   * and creating an account. That needs tenants and a server — see ROADMAP.md.
   * Until it exists, somebody is added by hand and arrives here.
   */
  /*
   * THE BOARD IS NEVER DRAWN WITHOUT A BOARD TO DRAW — and this is the part I
   * got wrong first time.
   *
   * "No projects" and "the list has not arrived" are the same empty list, and
   * only one of them is somebody who needs setting up. But the first version of
   * this simply let the not-yet-known case fall THROUGH, and the board had
   * never once been rendered with nothing to show: the setup wizard had always
   * stood in front of that state. It turns out not to survive it — Vinnie got a
   * white page and a React update-depth error on the test copy, 25 August 2026,
   * from a session whose projects never arrived.
   *
   * So the empty list is handled here in both of its meanings, and neither of
   * them reaches the board. Asked and empty is somebody to set up; not yet
   * asked is somebody to wait for.
   */
  if (projects.length === 0 && !projectsKnown)
    return (
      <>
        <EnvBanner />
        <div className="center-note">Loading the board…</div>
      </>
    );
  if (projects.length === 0) {
    return (
      <>
        <EnvBanner />
        <Welcome />
      </>
    );
  }

  /*
   * Worked out here rather than inside the button, because it depends on
   * whether the board it files onto exists at all — and a button that cannot
   * land is worse than no button once somebody has typed their report into it.
   */
  const showFeedback = offersFeedback({ projectId, projects });

  return (
    <div className="app-shell">
      <EnvBanner />
      {/* ONE STRIP ACROSS THE TOP for the update offer and the What's-new tour —
          merged and moved here from the foot of the sidebar (3.20.0), because the
          two small cards there went unnoticed. It drops the whole shell in on one
          transform; see UpdateBanner. Below the environment banner so the amber
          "not the live board" strip stays the topmost thing on a test copy. */}
      <UpdateBanner />
      {/* The top company strip was removed on 26 Sep — it duplicated the switcher
          two rows down, which now names the company on its own subline (see
          Switcher). One place says which company is on screen, not two. */}
      {/* A CARD EDIT THE DATABASE REFUSED, said out loud. The write is optimistic
          and the card's modal has already closed by the time a refusal comes
          back, so without this a save that did not save reverts in silence — the
          reported "I set the dates, saved, and they changed back". Above the
          layout so it is seen wherever the person now is, and it names the reason
          the database gave. See store `saveProblem`. */}
      {saveProblem && (
        <div className="save-problem" role="alert">
          <span className="save-problem-words">
            <Icon name="warning" size={14} />
            {saveProblem}
          </span>
          <button className="btn btn-small" onClick={clearSaveProblem}>
            Dismiss
          </button>
        </div>
      )}
      {/* A brief confirmation that a command palette action ran — a status set, a
          card assigned, a project switched — since the palette acts and closes in
          one keystroke and would otherwise leave nothing on screen. See `flash`. */}
      {flash && (
        <div className="flash-toast" role="status" aria-live="polite">
          {flash}
        </div>
      )}
      {/* THE HELP COACH-MARK — after a "how do I…" answer sends you to a screen, the
          one next step stays on screen until you've read it. Dismissible (the ×,
          Escape, or choosing another screen); unlike the flash toast it does not
          time out, because a sentence needs reading. See `coach` / rule P9. */}
      {coach && <CoachMark text={coach} onClose={() => showCoach(null)} />}
      {/* THE FIRST-RUN POINTER — once per browser, points at the Help row so a
          newcomer learns the help exists. Not shown over a help-answer coach or
          during forced setup. */}
      {helpIntro && !coach && !setupForced && (
        <CoachMark
          anchor=".nav-help-row"
          title="Need a hand?"
          text="Open Help here any time, or press ⌘K and type “how do I…”. Every answer takes you to the control."
          onClose={dismissIntro}
        />
      )}
      {/* The update offer and the What's-new tour now live in one strip at the
          top of the shell (UpdateBanner, above); they were fixed corner cards,
          then sidebar-foot cards (#268/#360), before merging in 3.20.0. */}
      {/* ALWAYS ON, ABOVE EVERYTHING, AND ALWAYS ABLE TO END ITSELF.
          A preview can hide screens — that is what it is for — so the way out
          of one must not live on a screen it can hide. */}
      {viewing && (
        <div className="preview-bar" role="status">
          <span className="preview-eye">
            <Icon name="search" size={13} />
          </span>
          <span className="preview-words">
            {previewBanner(
              displayName(viewing.email, profiles),
              projects.find((p) => p.id === projectId)?.name ?? 'this board',
              viewing.role
            )}
          </span>
          <button className="btn btn-small" onClick={stopPreview}>
            Stop
          </button>
        </div>
      )}
      <div className="layout">
        <aside className="sidebar">
          <div className="brand">
            <BrandHome />
            <VersionChip />
          </div>

          {/* One control for the board on screen and the company it belongs to —
              the board leads, the company is the subline, both lists live in its
              menu. Replaced the stacked WorkspaceSwitcher + ProjectSwitcher on
              26 Sep (mock S1). See Switcher. */}
          <Switcher />

          {sample.readOnly ? (
            /* On a sample board the control stays visible but inert, and a click
               gets the gentle note (mock 03) — not hidden, so a newcomer sees it
               exists. */
            <button
              className="btn btn-primary new-issue is-inert"
              onClick={() => void sample.note()}
              aria-disabled
              title={sample.reason}
            >
              <Icon name="plus" size={15} /> New issue
            </button>
          ) : canCreateIssues(role, source) ? (
            <button className="btn btn-primary new-issue" onClick={() => startCreate()}>
              <Icon name="plus" size={15} /> New issue
            </button>
          ) : (
            can(role, 'createIssue') && (
              /* A tracker-run board raises no issues in the app, so the slot the
                 New issue button would fill says what kind of board this is
                 instead — as a deliberate status in a recessed well, not a stray
                 grey line where a control should be. Same quiet family as the
                 new-version notice at the foot of the sidebar. */
              <div className="board-source">
                <Icon name="link" size={15} className="board-source-icon" />
                <div className="board-source-text">
                  <span className="board-source-label">Tracker-run board</span>
                  <p className="board-source-note">
                    Issues are raised in <strong>{sourceName}</strong> and copied here to read and comment on.
                  </p>
                </div>
              </div>
            )
          )}

          {/*
            "WORKSPACE" UNTIL 12 AUGUST, six inches below a switcher that uses
            the same word for a company. Two meanings of one noun that close
            together is worse than no label: the group beneath is the board on
            screen and the people who can reach it, not the company.

            The company is a WORKSPACE, everywhere, and nothing else is. That is
            the word the switcher, the account panel and the emails all use now.
          */}
          <nav>
            {(() => {
              const items = NAV.filter(navVisible);
              // The group the current view belongs to is never folded — a person
              // must always see where they are. Folding it is remembered and
              // takes effect the moment they move to another group. #sidebar-26Sep.
              const activeGroup = items.find((n) => n.view === view)?.group;
              // Children render nested under their parent, never as their own
              // top-level rows (R19). The top-level list is everything else.
              const childrenOf = (p: View) => items.filter((c) => c.parent === p);
              const topItems = items.filter((n) => !n.parent);

              // The badge a row carries, if any — kept in one place so a parent
              // and a (hypothetical) child render it the same way.
              const badgeFor = (v: View) => {
                if (v === 'mywork' && inboxWaiting > 0) return inboxWaiting;
                if (v === 'activity' && unread > 0) return unread;
                if (v === 'approvals' && signoffsWaiting > 0) return signoffsWaiting;
                // Both admin numbers live on People and access, decided by role —
                // an admin sees requests waiting on them; a member sees their own
                // answered asks. One number in one place for each — never both (#359).
                if (v === 'admin' && can(workspaceRole, 'manageWorkspace') && requestsPending > 0) return requestsPending;
                if (v === 'admin' && !can(workspaceRole, 'manageWorkspace') && myOutcomes > 0) return myOutcomes;
                return 0;
              };

              // One nav row — parent or child. A child is indented and smaller but
              // wears the same family (icon hue at rest, solid fill when active, R3).
              const navRow = (n: (typeof NAV)[number], isChild = false) => {
                const badge = badgeFor(n.view);
                return (
                  <button
                    key={n.view}
                    style={{ '--nav-c': `var(--nav-${n.view})` } as CSSProperties}
                    className={`nav-item ${isChild ? 'nav-child' : ''} ${view === n.view ? 'active' : ''}`}
                    onClick={() => setView(n.view)}
                  >
                    <span className="nav-icon">
                      <NavIcon view={n.view} size={isChild ? 16 : 18} />
                    </span>
                    <span className="nav-text">{n.label}</span>
                    {badge > 0 && <span className="nav-badge">{badge}</span>}
                  </button>
                );
              };

              // One top-level row — a lone nav row, or a parent with its own
              // sub-menu. The sub-menu opens when you are on the parent or one of
              // its children (so clicking Plan reveals Budget & payments), or when
              // you opened it by hand; it now folds on the shared Expander — kept
              // mounted but inert while closed — rather than leaving the DOM, so it
              // slides shut as smoothly as it slides open (Vinnie, 25 Oct).
              const renderItem = (n: (typeof NAV)[number]) => {
                const kids = childrenOf(n.view);
                const childActive = kids.some((k) => k.view === view);
                const autoOpen = view === n.view || childActive;
                const navIntent = expandedNav.get(n.view);
                const subOpen = kids.length > 0 && (navIntent === undefined ? autoOpen : navIntent);
                if (kids.length === 0) return navRow(n);
                return (
                  <div className="nav-parent" key={n.view}>
                    {/* The parent row NAVIGATES to its own screen (clicking Plan
                        opens the Plan); a separate disclosure toggles the sub-menu
                        without navigating. When a child is the active view the
                        parent reads "on trail" (quiet) but never also fills — only
                        the active leaf fills solid. */}
                    <button
                      style={{ '--nav-c': `var(--nav-${n.view})` } as CSSProperties}
                      className={`nav-item nav-has-sub ${view === n.view ? 'active' : ''} ${childActive ? 'is-ontrail' : ''}`}
                      onClick={() => setView(n.view)}
                    >
                      <span className="nav-icon">
                        <NavIcon view={n.view} size={18} />
                      </span>
                      <span className="nav-text">{n.label}</span>
                      {badgeFor(n.view) > 0 && <span className="nav-badge">{badgeFor(n.view)}</span>}
                      <span
                        className={`nav-disc ${subOpen ? 'is-open' : ''}`}
                        role="button"
                        tabIndex={0}
                        aria-label={`${subOpen ? 'Hide' : 'Show'} ${n.label} sub-menu`}
                        aria-expanded={subOpen}
                        onClick={(e) => { e.stopPropagation(); toggleNavParent(n.view, subOpen); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleNavParent(n.view, subOpen);
                          }
                        }}
                      >
                        <Icon name="chevron" size={18} />
                      </span>
                    </button>
                    <Expander open={subOpen}>
                      <div className="nav-sub">
                        {kids.map((k) => navRow(k, true))}
                      </div>
                    </Expander>
                  </div>
                );
              };

              // Group the top-level rows in NAV order so each group's rows fold
              // together as one block on the Expander. The active group never folds
              // — a person must always see where they are (#sidebar-26Sep).
              const groupOrder: string[] = [];
              const byGroup = new Map<string, typeof topItems>();
              for (const n of topItems) {
                if (!byGroup.has(n.group)) { byGroup.set(n.group, []); groupOrder.push(n.group); }
                byGroup.get(n.group)!.push(n);
              }

              return groupOrder.map((group) => {
                const groupItems = byGroup.get(group)!;
                // MyWork is a lone row with no header (nothing to fold); Settings is
                // a lone row with only air above it — a gap, no header.
                if (group === 'MyWork') {
                  return <Fragment key={group}>{groupItems.map(renderItem)}</Fragment>;
                }
                if (group === 'Settings') {
                  return (
                    <Fragment key={group}>
                      <div className="nav-gap" aria-hidden="true" />
                      {groupItems.map(renderItem)}
                    </Fragment>
                  );
                }
                const folded = !isPhone && group !== activeGroup && collapsedGroups.has(group);
                return (
                  <Fragment key={group}>
                    {/* The label IS the toggle — no separate control — so the caret
                        turns down when open, right when folded. The active group
                        always reads open (folded is false above). */}
                    <button
                      type="button"
                      className="nav-group-head"
                      aria-expanded={!folded}
                      onClick={() => toggleGroup(group)}
                    >
                      <Icon name="chevron" size={18} className="nav-group-caret" />
                      <span className="nav-label">{group}</span>
                    </button>
                    <Expander open={!folded}>{groupItems.map(renderItem)}</Expander>
                  </Fragment>
                );
              });
            })()}
            {/* HELP — a lone nav row under Settings (Vinnie, 1 Oct 2026: a nav row,
                not a foot button). It is not a screen in NAV; it opens the Help
                screen (one surface, two doors with ⌘K — P9). Same row treatment as
                every other nav item, with its own quiet hue. (It is NOT pinned —
                a sticky pin overlapped the rows above it in Safari, and Vinnie's
                call on 15 Oct was to leave it a plain last row for now.) */}
            <button
              type="button"
              style={{ '--nav-c': 'var(--nav-help)' } as CSSProperties}
              className={`nav-item nav-help-row ${view === 'help' ? 'active' : ''}`}
              data-reveal="help-row"
              onClick={() => {
                dismissIntro();
                setView('help');
              }}
            >
              <span className="nav-icon">
                <Icon name="help" size={18} />
              </span>
              <span className="nav-text">Help</span>
            </button>
          </nav>

          {/* AT THE BOTTOM, NOT IN THE NAVIGATION.
              It is not a place to go — it is something to do, and something a
              tester does from wherever they happen to be when they find it.
              Above the account panel because it belongs to the work rather
              than to who you are. */}
          {showFeedback && (
            <button className="btn feedback-open" onClick={() => setFeedbackOpen(true)}>
              <Icon name="comment" size={15} /> Feedback
            </button>
          )}

          {/* THE ACCOUNT ROW sits at the foot of the sidebar. The update offer and
              the What's-new tour that used to live here as two small cards (#268,
              #360) were unnoticed, so in 3.20.0 they merged into one strip across
              the top of the shell — see UpdateBanner, mounted after EnvBanner. The
              "Live updates have stopped" card (#308) that stacked above them was
              removed in the same change; the live seam re-reads on reconnect on
              its own. */}
          <div className="sidebar-bottom">
            <div className="sidebar-account">
              <AccountPanel />
            </div>
          </div>
        </aside>

        <main className="main">
          {onSampleScreen && <SampleStrip />}
          <header className="topbar">
            <div className="topbar-heading">
              {!OWNS_HEADER.has(view) && <h1>{VIEW_TITLES[view]}</h1>}
            </div>
            {(sample.readOnly || canCreateIssues(role, source)) && (
              <button
                className={`btn btn-primary topbar-new ${sample.readOnly ? 'is-inert' : ''}`}
                onClick={() => (sample.readOnly ? void sample.note() : startCreate())}
                aria-disabled={sample.readOnly || undefined}
                title={sample.readOnly ? sample.reason : 'New issue'}
              >
                <Icon name="plus" size={15} />
                <span className="topbar-new-word">New issue</span>
              </button>
            )}
            {(view === 'board' || view === 'backlog' || view === 'releases' || view === 'documents') && (
              <div className="search-wrap">
                <Icon name="search" size={14} />
                <input
                  className="search"
                  placeholder={
                    view === 'releases'
                      ? 'Search builds…'
                      : view === 'documents'
                        ? 'Search documents…'
                        : 'Search issues…'
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {/* The badge is the only thing that tells anybody the shortcut
                    exists. It is also a button, because a hint you cannot press
                    is no use to somebody who does not use shortcuts. */}
                <button
                  className="search-kbd"
                  onClick={() => setPaletteOpen(true)}
                  aria-label="Go to anything (Command or Ctrl K)"
                  title="Go to anything"
                >
                  {combo('K')}
                </button>
              </div>
            )}
            <NotificationBell />
            <AccountMenu />
          </header>
          {/*
            * THE WAY BACK, ON ITS OWN ROW UNDER THE TITLE.
            *
            * Vinnie, 28 August 2026: *"The back to insights button should be
            * under the screen title, and properly spaced between the title and
            * anything below it."*
            *
            * OUTSIDE THE TOP BAR RATHER THAN UNDER THE TITLE INSIDE IT. The top
            * bar is a centred row, so a second line in the heading lifts the
            * title by half the link's height — the name of the page you are on
            * would sit at a different height depending on whether you drilled
            * in. It was an eyebrow ABOVE the title for the mirror image of that
            * reason, and this keeps the fix while doing what was asked.
            *
            * Outside `.view` too, which is keyed on the view and re-created on
            * every move: inside it, the link would replay the arrival animation
            * and read as part of the screen rather than as the way off it.
            */}
          <WayBack />
          {/* Keyed on the view, so React replaces this element when you move
              between screens rather than reusing it. Without a new element there
              is nothing for the arrival animation to run on, and the panel
              simply swaps — which is what it did before. */}
          <div className="view view-swap" key={view}>
            {view === 'mywork' && <MyWork />}
            {view === 'board' && <Board />}
            {view === 'plan' && isProject && <PlanView />}
            {view === 'riskregister' && isProject && <RiskRegisterView />}
            {view === 'decisions' && isProject && <DecisionsView />}
            {view === 'lessons' && isProject && <LessonsView />}
            {view === 'lessonslibrary' && <LessonsLibrary />}
            {view === 'budgetpay' && <BudgetPay />}
            {view === 'sprints' && <Sprints />}
            {view === 'backlog' && <Backlog />}
            {view === 'releases' && <Releases />}
            {view === 'documents' && <Documents />}
            {view === 'activity' && <Activity />}
            {view === 'dashboard' && <Dashboard />}
            {/* A CAPABILITY-GATED VIEW NEVER RENDERS BLANK. These three screens
                show only to a role that holds the capability, and the nav hides
                them otherwise — but a help/⌘K "Take me there" (or a stale saved
                view) can still land someone here, and with no else-branch the
                panel came up empty, title and all (interaction-witness #1, 15 Oct
                2026). Explain it instead, so the arrival is never a blank page. */}
            {view === 'portfolio' &&
              (can(role, 'editSettings') ? (
                <Portfolio />
              ) : (
                <div className="bl-empty">The portfolio view isn’t open to your role. Ask a project manager or an admin if you need it.</div>
              ))}
            {view === 'payments' &&
              (can(role, 'editSettings') ? (
                <Payments />
              ) : (
                <div className="bl-empty">This finance screen isn’t open to your role. Ask a project manager or an admin if you need it.</div>
              ))}
            {view === 'payapproval' &&
              (can(role, 'manageWorkspace') ? (
                <PaymentApproval />
              ) : (
                <div className="bl-empty">Payment approval is set by a workspace admin. Ask one of your workspace’s admins to set or change who approves payments.</div>
              ))}
            {view === 'settings' && <Settings />}
            {view === 'help' && <HelpView />}
          {view === 'approvals' && <Approvals />}
          {/* Legacy route. 'requests' was its own screen until it became a tab
              inside People and access; anyone who kept the old view name in
              local storage is redirected into that screen, opened on Requests.
              A member who lands here falls to their own People-and-access
              screen, exactly as the 'admin' route does. #359. */}
          {view === 'requests' && (can(workspaceRole, 'setRoles') ? <Admin initialTab="requests" /> : <MemberPeople />)}
          {view === 'admin' && <WorkspaceHub />}
          {view === 'account' && <Account />}
          </div>
        </main>

        {/* Keyed by the open card so switching from one card straight to another
            (a blocker link, a bell, My Work) REMOUNTS it — its buffered edit-form
            state is seeded per card and would otherwise bleed the previous card's
            values into the next one's editor (N1). */}
        {(selectedId !== null || creating) && <IssueModal key={selectedId ?? 'new'} />}
        {/* Mounted here rather than inside the project switcher that offers it.
            `.overlay` is positioned against its nearest positioned ancestor, so
            rendered from the sidebar it filled the SIDEBAR — a whole screen's
            worth of form squeezed into 216 pixels. */}
        {startingBoard && <NewBoardFlow close={() => setStartingBoard(false)} />}
        {/* ASKED FOR, NEVER IMPOSED.
            It used to open by itself on any project nobody had set up. That
            meant switching project mid-task threw six questions over whatever
            was on the screen — including a card just opened — and the work
            underneath was unreachable until they were answered or skipped.
            The board offers it instead, on a line that can be waved away, and
            this only opens when somebody says yes. */}
        {setupForced && <SetupWizard />}
        {feedbackOpen && <Feedback close={() => setFeedbackOpen(false)} />}
        {/* Driven by the store rather than by a button: it appears because a
            card moved, which can happen from the board or from a card. */}
        <WhyClosed />
        {paletteOpen && (
          <Palette
            places={NAV.filter(navVisible).map((n) => ({ view: n.view, label: n.label }))}
            mode={paletteMode}
            close={() => setPaletteOpen(false)}
          />
        )}
        {/* LAST, so the in-app confirm/prompt paints over every other overlay —
            a card, a wizard, the palette — whatever opened it. Driven by
            confirm.ts; renders nothing until something is queued. */}
        <ConfirmHost />
      </div>
    </div>
  );
}
